import { API_HOST } from '@/constants/config';

// SSR-safe explore API client used by `generateStaticParams` and other
// build-time SEO paths. Mirrors `lib/blogApi.js` — direct fetch (no auth,
// no Zustand) so it works inside Node during the Expo Router static export.
//
// Runtime/auth-bound calls still live in `lib/api.js` (`exploreApi`).

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const fetchExplore = async (path, params = {}) => {
  const url = `${API_HOST}${path}${toQueryString(params)}`;
  let response;

  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Platform': 'web',
      },
    });
  } catch (networkErr) {
    throw new Error('Network error — unable to reach the explore service.');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      response.ok
        ? 'Explore service returned an unexpected response.'
        : `Explore service returned ${response.status}.`,
    );
  }

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.message || 'Failed to fetch explore data');
  }

  return data;
};

export const exploreApiSsr = {
  // Public feed listing — the source of truth for which item ids get
  // pre-rendered as static SEO pages. We sort by featured (the default) so the
  // sitemap-prerendered subset is the "best" content the team has curated.
  listItems: async ({ page = 1, limit = 100, sort = 'featured', mediaType } = {}) => {
    const data = await fetchExplore('/json/explore', { page, limit, sort, mediaType });
    return {
      items: data.items || [],
      pagination: data.pagination || {},
    };
  },

  // Single explore item by slug. Used to enrich per-item MetaHead at build
  // time (and as a fallback at runtime if the item wasn't in the prerender
  // cache).
  getItemBySlug: async (slug) => {
    if (!slug) return null;
    const data = await fetchExplore(`/json/explore/${encodeURIComponent(slug)}`);
    return data.item || null;
  },

  // Public list of enabled explore categories. Drives `generateStaticParams`
  // for /explore/category/[slug] and the per-category MetaHead built at the
  // top of the page (title/description/JSON-LD/featured image).
  listCategories: async ({ withFeatured = true, withCounts = true } = {}) => {
    const data = await fetchExplore('/json/explore/categories', {
      ...(withFeatured ? { withFeatured: 'true' } : {}),
      ...(withCounts ? { withCounts: 'true' } : {}),
    });
    return { categories: data.categories || [] };
  },
};
